import React, { useState, useEffect } from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Toolbar, Typography, Grid, Card, CardContent, CardActionArea, Divider, IconButton } from '@mui/material';
import Icon from '@mui/material/Icon';
import CodeIcon from '@mui/icons-material/Code';
import Footer from './Footer';
import AdUnit from './AdUnit';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BuildIcon from '@mui/icons-material/Build';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import SchoolIcon from '@mui/icons-material/School';
import HomeIcon from '@mui/icons-material/Home';
import JsonBeautifier from './tools/JsonBeautifier';
import XmlBeautifier from './tools/XmlBeautifier';
import TomlBeautifier from './tools/TomlBeautifier';
import SqlBeautifier from './tools/SqlBeautifier';
import HtmlBeautifier from './tools/HtmlBeautifier';
import CssBeautifier from './tools/CssBeautifier';
import JavascriptBeautifier from './tools/JavascriptBeautifier';
import IniBeautifier from './tools/IniBeautifier';
import JsonMinifier from './tools/JsonMinifier';
import XmlMinifier from './tools/XmlMinifier';
import CssMinifier from './tools/CssMinifier';
import JavascriptMinifier from './tools/JavascriptMinifier';
import HtmlMinifier from './tools/HtmlMinifier';
import JsonToYamlConverter from './tools/JsonToYamlConverter';
import JsonToTomlConverter from './tools/JsonToTomlConverter';
import JsonToCsvConverter from './tools/JsonToCsvConverter';
import CsvToTsvConverter from './tools/CsvToTsvConverter';
import JsonToIniConverter from './tools/JsonToIniConverter';
import JsonToSqlConverter from './tools/JsonToSqlConverter';
import HtmlToJsxConverter from './tools/HtmlToJsxConverter';
import MarkdownToHtmlConverter from './tools/MarkdownToHtmlConverter';
import Base64EncoderDecoder from './tools/Base64EncoderDecoder';
import UrlEncoderDecoder from './tools/UrlEncoderDecoder';
import HtmlEntitiesEncoderDecoder from './tools/HtmlEntitiesEncoderDecoder';
import JwtDecoderEncoder from './tools/JwtDecoderEncoder';
import AesEncryptDecrypt from './tools/AesEncryptDecrypt';
import StringToHexConverter from './tools/StringToHexConverter';
import StringToBinaryConverter from './tools/StringToBinaryConverter';
import UrlParser from './tools/UrlParser';
import Md5Generator from './tools/Md5Generator';
import Sha1Generator from './tools/Sha1Generator';
import Sha256Generator from './tools/Sha256Generator';
import Sha3Generator from './tools/Sha3Generator';
import Sha512Generator from './tools/Sha512Generator';
import BcryptGenerator from './tools/BcryptGenerator';
import UuidGenerator from './tools/UuidGenerator';
import PasswordGenerator from './tools/PasswordGenerator';
import HtaccessGenerator from './tools/HtaccessGenerator';
import QrCodeGenerator from './tools/QrCodeGenerator';
import MorseCodeConverter from './tools/MorseCodeConverter';
import RobotsTxtGenerator from './tools/RobotsTxtGenerator';
import SitemapGenerator from './tools/SitemapGenerator';
import LoremIpsumGenerator from './tools/LoremIpsumGenerator';
import DummyJsonGenerator from './tools/DummyJsonGenerator';
import DockerComposeGenerator from './tools/DockerComposeGenerator';
import GitCommitGenerator from './tools/GitCommitGenerator';
import ColorContrastChecker from './tools/ColorContrastChecker';
import ImageToBase64 from './tools/ImageToBase64';
import VariableCaseConverter from './tools/VariableCaseConverter';
import WordCounter from './tools/WordCounter';
import FindAndReplace from './tools/FindAndReplace';
import TextSorter from './tools/TextSorter';
import TextDeduplicator from './tools/TextDeduplicator';
import LineReverser from './tools/LineReverser';
import PrefixSuffixAdder from './tools/PrefixSuffixAdder';
import CronGenerator from './tools/CronGenerator';
import DurationCalculator from './tools/DurationCalculator';
import TimestampConverter from './tools/TimestampConverter';
import DataSizeConverter from './tools/DataSizeConverter';
import UnitConverter from './tools/UnitConverter';
import NumberBaseConverter from './tools/NumberBaseConverter';
import BitwiseCalculator from './tools/BitwiseCalculator';
import CodeComplexityAnalyzer from './tools/CodeComplexityAnalyzer';
import ChmodCalculator from './tools/ChmodCalculator';
import Ipv4ToIpv6 from './tools/Ipv4ToIpv6';
import MimeLookup from './tools/MimeLookup';
import UserAgentParser from './tools/UserAgentParser';
import RegexTester from './tools/RegexTester';
import CodeDiffMerge from './tools/CodeDiffMerge';
import HttpHeadersParser from './tools/HttpHeadersParser';
import HttpStatusLookup from './tools/HttpStatusLookup';
import AsciiTable from './tools/AsciiTable';
import GitCheatSheet from './tools/GitCheatSheet';

const drawerWidth = 280;

const categoryIcons = {
  'code': <CodeIcon />,
  'data': <StorageIcon />,
  'network': <SettingsEthernetIcon />,
  'time-number': <AccessTimeIcon />,
  'generators': <AutoFixHighIcon />,
  'developer': <BuildIcon />,
  'accessibility': <AccessibilityIcon />,
  'student': <SchoolIcon />
};

const MainContent = ({ search, mobileOpen, setMobileOpen, currentRoute }) => {
  const toolsData = {
  "categories": [
    {
      "id": "code",
      "displayName": "Code Tools",
      "icon": "fas fa-code"
    },
    {
      "id": "data",
      "displayName": "Data Tools",
      "icon": "fas fa-database"
    },
    {
      "id": "network",
      "displayName": "Network Tools",
      "icon": "fas fa-network-wired"
    },
    {
      "id": "time-number",
      "displayName": "Time & Number Tools",
      "icon": "fas fa-clock"
    },
    {
      "id": "generators",
      "displayName": "Generators",
      "icon": "fas fa-magic"
    },
    {
      "id": "developer",
      "displayName": "Developer Tools",
      "icon": "fas fa-tools"
    },
    {
      "id": "accessibility",
      "displayName": "Accessibility Tools",
      "icon": "fas fa-universal-access"
    },
    {
      "id": "student",
      "displayName": "Student Tools",
      "icon": "fas fa-user-graduate"
    }
  ],
  "tools": [
    {
      "title": "JSON Beautifier",
      "icon": "fas fa-code",
      "description": "Format and beautify your JSON code.",
      "url": "/code/formatters/json",
      "category": "code",
      "componentName": "JsonBeautifier",
      "isImplemented": true
    },
    {
      "title": "XML Beautifier",
      "icon": "fas fa-code",
      "description": "Format and beautify your XML code.",
      "url": "/code/formatters/xml",
      "category": "code",
      "componentName": "XmlBeautifier",
      "isImplemented": true
    },
    {
      "title": "YAML Beautifier",
      "icon": "fas fa-code",
      "description": "Format and beautify your YAML code.",
      "url": "/code/formatters/yaml",
      "category": "code",
      "componentName": "YamlBeautifier",
      "isImplemented": false
    },
    {
      "title": "TOML Beautifier",
      "icon": "fas fa-code",
      "description": "Format and beautify your TOML code.",
      "url": "/code/formatters/toml",
      "category": "code",
      "componentName": "TomlBeautifier",
      "isImplemented": true
    },
    {
      "title": "SQL Beautifier",
      "icon": "fas fa-database",
      "description": "Format and beautify your SQL queries.",
      "url": "/code/formatters/sql",
      "category": "code",
      "componentName": "SqlBeautifier",
      "isImplemented": true
    },
    {
      "title": "HTML Beautifier",
      "icon": "fab fa-html5",
      "description": "Format and beautify your HTML code.",
      "url": "/code/formatters/html",
      "category": "code",
      "componentName": "HtmlBeautifier",
      "isImplemented": true
    },
    {
      "title": "CSS Beautifier",
      "icon": "fab fa-css3-alt",
      "description": "Format and beautify your CSS code.",
      "url": "/code/formatters/css",
      "category": "code",
      "componentName": "CssBeautifier",
      "isImplemented": true
    },
    {
      "title": "JavaScript Beautifier",
      "icon": "fab fa-js",
      "description": "Format and beautify your JavaScript code.",
      "url": "/code/formatters/javascript",
      "category": "code",
      "componentName": "JavascriptBeautifier",
      "isImplemented": true
    },
    {
      "title": "INI Beautifier",
      "icon": "fas fa-cog",
      "description": "Format and beautify your INI configuration files.",
      "url": "/code/formatters/ini",
      "category": "code",
      "componentName": "IniBeautifier",
      "isImplemented": true
    },
    {
      "title": "Properties Beautifier",
      "icon": "fas fa-file-alt",
      "description": "Format and beautify your Properties files.",
      "url": "/code/formatters/properties",
      "category": "code",
      "componentName": "PropertiesBeautifier",
      "isImplemented": false
    },
    {
      "title": "Dockerfile Beautifier",
      "icon": "fab fa-docker",
      "description": "Format and beautify your Dockerfiles.",
      "url": "/code/formatters/dockerfile",
      "category": "code",
      "componentName": "DockerfileBeautifier",
      "isImplemented": false
    },
    {
      "title": "Bash Script Beautifier",
      "icon": "fas fa-terminal",
      "description": "Format and beautify your Bash scripts.",
      "url": "/code/formatters/bash",
      "category": "code",
      "componentName": "BashScriptBeautifier",
      "isImplemented": false
    },
    {
      "title": "JSON Minifier",
      "icon": "fas fa-compress-alt",
      "description": "Minify your JSON code to reduce file size.",
      "url": "/code/minifiers/json",
      "category": "code",
      "componentName": "JsonMinifier",
      "isImplemented": true
    },
    {
      "title": "XML Minifier",
      "icon": "fas fa-compress-alt",
      "description": "Minify your XML code to reduce file size.",
      "url": "/code/minifiers/xml",
      "category": "code",
      "componentName": "XmlMinifier",
      "isImplemented": true
    },
    {
      "title": "CSS Minifier",
      "icon": "fas fa-compress-alt",
      "description": "Minify your CSS code to reduce file size.",
      "url": "/code/minifiers/css",
      "category": "code",
      "componentName": "CssMinifier",
      "isImplemented": true
    },
    {
      "title": "JavaScript Minifier",
      "icon": "fas fa-compress-alt",
      "description": "Minify your JavaScript code.",
      "url": "/code/minifiers/javascript",
      "category": "code",
      "componentName": "JavascriptMinifier",
      "isImplemented": true
    },
    {
      "title": "HTML Minifier",
      "icon": "fas fa-compress-alt",
      "description": "Minify your HTML code.",
      "url": "/code/minifiers/html",
      "category": "code",
      "componentName": "HtmlMinifier",
      "isImplemented": true
    },
    {
      "title": "GraphQL Formatter",
      "icon": "fas fa-project-diagram",
      "description": "Format your GraphQL queries.",
      "url": "/code/formatters/graphql",
      "category": "code",
      "componentName": "GraphqlFormatter",
      "isImplemented": false
    },
    {
      "title": "JSON to YAML Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert between JSON and YAML formats.",
      "url": "/code/converters/json-yaml",
      "category": "code",
      "componentName": "JsonToYamlConverter",
      "isImplemented": true
    },
    {
      "title": "JSON to TOML Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert between JSON and TOML formats.",
      "url": "/code/converters/json-toml",
      "category": "code",
      "componentName": "JsonToTomlConverter",
      "isImplemented": true
    },
    {
      "title": "JSON to CSV Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert between JSON and CSV formats.",
      "url": "/code/converters/json-csv",
      "category": "code",
      "componentName": "JsonToCsvConverter",
      "isImplemented": true
    },
    {
      "title": "CSV to TSV Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert between CSV and TSV formats.",
      "url": "/code/converters/csv-tsv",
      "category": "code",
      "componentName": "CsvToTsvConverter",
      "isImplemented": true
    },
    {
      "title": "JSON to Properties Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert JSON to Properties.",
      "url": "/code/converters/json-properties",
      "category": "code",
      "componentName": "JsonToPropertiesConverter",
      "isImplemented": false
    },
    {
      "title": "JSON to INI Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert JSON to INI.",
      "url": "/code/converters/json-ini",
      "category": "code",
      "componentName": "JsonToIniConverter",
      "isImplemented": true
    },
    {
      "title": "JSON to SQL Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert JSON data to SQL INSERT statements.",
      "url": "/code/converters/json-sql",
      "category": "data",
      "componentName": "JsonToSqlConverter",
      "isImplemented": true
    },
    {
      "title": "Regex Code Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert regex between languages.",
      "url": "/code/converters/regex-code",
      "category": "code",
      "componentName": "RegexCodeConverter",
      "isImplemented": false
    },
    {
      "title": "Pseudocode Converter",
      "icon": "fas fa-code",
      "description": "Convert pseudocode to programming languages.",
      "url": "/code/converters/pseudocode",
      "category": "student",
      "componentName": "PseudocodeConverter",
      "isImplemented": false
    },
    {
      "title": "CSS to Tailwind Converter",
      "icon": "fab fa-css3",
      "description": "Convert Vanilla CSS to Tailwind classes.",
      "url": "/code/converters/css-tailwind",
      "category": "code",
      "componentName": "CssToTailwindConverter",
      "isImplemented": false
    },
    {
      "title": "HTML to JSX Converter",
      "icon": "fab fa-react",
      "description": "Convert HTML to React JSX.",
      "url": "/code/converters/html-jsx",
      "category": "code",
      "componentName": "HtmlToJsxConverter",
      "isImplemented": true
    },
    {
      "title": "JSON to TypeScript",
      "icon": "fab fa-js",
      "description": "Generate TS interfaces from JSON.",
      "url": "/code/converters/json-ts",
      "category": "code",
      "componentName": "JsonToTsConverter",
      "isImplemented": false
    },
    {
      "title": "Markdown to HTML",
      "icon": "fab fa-markdown",
      "description": "Convert Markdown to HTML.",
      "url": "/code/converters/markdown-html",
      "category": "code",
      "componentName": "MarkdownToHtmlConverter",
      "isImplemented": true
    },
    {
      "title": "HTML to Markdown",
      "icon": "fab fa-html5",
      "description": "Convert HTML to Markdown.",
      "url": "/code/converters/html-markdown",
      "category": "code",
      "componentName": "HtmlToMarkdownConverter",
      "isImplemented": false
    },
    {
      "title": "Base64 Encoder / Decoder",
      "icon": "fas fa-lock",
      "description": "Encode or decode using Base64.",
      "url": "/encoders/base64",
      "category": "code",
      "componentName": "Base64EncoderDecoder",
      "isImplemented": true
    },
    {
      "title": "URL Encoder / Decoder",
      "icon": "fas fa-link",
      "description": "Encode or decode URLs.",
      "url": "/encoders/url",
      "category": "code",
      "componentName": "UrlEncoderDecoder",
      "isImplemented": true
    },
    {
      "title": "HTML Entities Encoder / Decoder",
      "icon": "fas fa-code",
      "description": "Encode or decode HTML entities.",
      "url": "/encoders/html-entities",
      "category": "code",
      "componentName": "HtmlEntitiesEncoderDecoder",
      "isImplemented": true
    },
    {
      "title": "JWT Decoder / Encoder",
      "icon": "fas fa-key",
      "description": "Decode or encode JSON Web Tokens.",
      "url": "/encoders/jwt",
      "category": "code",
      "componentName": "JwtDecoderEncoder",
      "isImplemented": true
    },
    {
      "title": "AES Encrypt / Decrypt",
      "icon": "fas fa-shield-alt",
      "description": "Encrypt or decrypt using AES.",
      "url": "/encoders/aes",
      "category": "code",
      "componentName": "AesEncryptDecrypt",
      "isImplemented": true
    },
    {
      "title": "RSA Encrypt / Decrypt",
      "icon": "fas fa-key",
      "description": "Encrypt or decrypt using RSA keys.",
      "url": "/encoders/rsa",
      "category": "code",
      "componentName": "RsaEncryptDecrypt",
      "isImplemented": false
    },
    {
      "title": "HMAC Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate HMAC codes.",
      "url": "/encoders/hmac",
      "category": "code",
      "componentName": "HmacGenerator",
      "isImplemented": false
    },
    {
      "title": "Bcrypt Hash Checker",
      "icon": "fas fa-check-circle",
      "description": "Verify a text against a bcrypt hash.",
      "url": "/encoders/bcrypt-checker",
      "category": "code",
      "componentName": "BcryptChecker",
      "isImplemented": false
    },
    {
      "title": "Text Obfuscator",
      "icon": "fas fa-eye-slash",
      "description": "Obfuscate text.",
      "url": "/encoders/text-obfuscator",
      "category": "code",
      "componentName": "TextObfuscator",
      "isImplemented": false
    },
    {
      "title": "String to Hex Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert strings to Hex values.",
      "url": "/encoders/string-hex",
      "category": "code",
      "componentName": "StringToHexConverter",
      "isImplemented": true
    },
    {
      "title": "String to Binary Converter",
      "icon": "fas fa-exchange-alt",
      "description": "Convert strings to Binary values.",
      "url": "/encoders/string-binary",
      "category": "code",
      "componentName": "StringToBinaryConverter",
      "isImplemented": true
    },
    {
      "title": "URL Parser",
      "icon": "fas fa-unlink",
      "description": "Parse URLs into components.",
      "url": "/encoders/url-parser",
      "category": "network",
      "componentName": "UrlParser",
      "isImplemented": true
    },
    {
      "title": "MD5 Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate MD5 hashes.",
      "url": "/hashes/md5",
      "category": "code",
      "componentName": "Md5Generator",
      "isImplemented": true
    },
    {
      "title": "SHA1 Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate SHA1 hashes.",
      "url": "/hashes/sha1",
      "category": "code",
      "componentName": "Sha1Generator",
      "isImplemented": true
    },
    {
      "title": "SHA256 Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate SHA256 hashes.",
      "url": "/hashes/sha256",
      "category": "code",
      "componentName": "Sha256Generator",
      "isImplemented": true
    },
    {
      "title": "SHA3 Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate SHA3 hashes.",
      "url": "/hashes/sha3",
      "category": "code",
      "componentName": "Sha3Generator",
      "isImplemented": true
    },
    {
      "title": "SHA512 Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate SHA512 hashes.",
      "url": "/hashes/sha512",
      "category": "code",
      "componentName": "Sha512Generator",
      "isImplemented": true
    },
    {
      "title": "RIPEMD160 Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate RIPEMD160 hashes.",
      "url": "/hashes/ripemd160",
      "category": "code",
      "componentName": "Ripemd160Generator",
      "isImplemented": false
    },
    {
      "title": "CRC32 Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate CRC32 hashes.",
      "url": "/hashes/crc32",
      "category": "code",
      "componentName": "Crc32Generator",
      "isImplemented": false
    },
    {
      "title": "Bcrypt Hash Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate Bcrypt hashes.",
      "url": "/hashes/bcrypt",
      "category": "code",
      "componentName": "BcryptGenerator",
      "isImplemented": true
    },
    {
      "title": "UUID Generator",
      "icon": "fas fa-fingerprint",
      "description": "Generate UUIDs v1, v3, v4, v5.",
      "url": "/generators/uuid",
      "category": "generators",
      "componentName": "UuidGenerator",
      "isImplemented": true
    },
    {
      "title": "Password Generator",
      "icon": "fas fa-key",
      "description": "Generate secure passwords.",
      "url": "/generators/password",
      "category": "generators",
      "componentName": "PasswordGenerator",
      "isImplemented": true
    },
    {
      "title": "Htaccess Generator",
      "icon": "fas fa-file-code",
      "description": "Generate basic .htaccess setups.",
      "url": "/generators/htaccess",
      "category": "generators",
      "componentName": "HtaccessGenerator",
      "isImplemented": true
    },
    {
      "title": "Barcode Generator",
      "icon": "fas fa-barcode",
      "description": "Generate barcodes visually.",
      "url": "/generators/barcode",
      "category": "generators",
      "componentName": "BarcodeGenerator",
      "isImplemented": false
    },
    {
      "title": "QR Code Generator",
      "icon": "fas fa-qrcode",
      "description": "Generate QR codes.",
      "url": "/generators/qrcode",
      "category": "generators",
      "componentName": "QrCodeGenerator",
      "isImplemented": true
    },
    {
      "title": "Morse Code Converter",
      "icon": "fas fa-signal",
      "description": "Convert to/from Morse code.",
      "url": "/generators/morse",
      "category": "generators",
      "componentName": "MorseCodeConverter",
      "isImplemented": true
    },
    {
      "title": "Meta Tag Generator",
      "icon": "fas fa-code",
      "description": "Generate HTML Meta Tags.",
      "url": "/generators/meta-tags",
      "category": "developer",
      "componentName": "MetaTagGenerator",
      "isImplemented": false
    },
    {
      "title": "Robots.txt Generator",
      "icon": "fas fa-robot",
      "description": "Generate robots.txt.",
      "url": "/generators/robots-txt",
      "category": "developer",
      "componentName": "RobotsTxtGenerator",
      "isImplemented": true
    },
    {
      "title": "Sitemap Generator",
      "icon": "fas fa-sitemap",
      "description": "Generate a basic sitemap.",
      "url": "/generators/sitemap",
      "category": "developer",
      "componentName": "SitemapGenerator",
      "isImplemented": true
    },
    {
      "title": "Lorem Ipsum Generator",
      "icon": "fas fa-align-left",
      "description": "Generate placeholder text.",
      "url": "/generators/lorem-ipsum",
      "category": "generators",
      "componentName": "LoremIpsumGenerator",
      "isImplemented": true
    },
    {
      "title": "Dummy JSON Generator",
      "icon": "fas fa-database",
      "description": "Generate mock JSON data.",
      "url": "/generators/dummy-json",
      "category": "generators",
      "componentName": "DummyJsonGenerator",
      "isImplemented": true
    },
    {
      "title": "SSH Key Pair Generator",
      "icon": "fas fa-key",
      "description": "Generate SSH keys locally.",
      "url": "/generators/ssh-key",
      "category": "generators",
      "componentName": "SshKeyGenerator",
      "isImplemented": false
    },
    {
      "title": "Docker-Compose Boilerplate Generator",
      "icon": "fab fa-docker",
      "description": "Generate standard docker-compose files.",
      "url": "/generators/docker-compose",
      "category": "generators",
      "componentName": "DockerComposeGenerator",
      "isImplemented": true
    },
    {
      "title": "API Request Builder",
      "icon": "fas fa-plug",
      "description": "Generate Fetch/Axios code.",
      "url": "/generators/api-request",
      "category": "developer",
      "componentName": "ApiRequestBuilder",
      "isImplemented": false
    },
    {
      "title": "Git Commit Generator",
      "icon": "fab fa-git-alt",
      "description": "Generate conventional commits.",
      "url": "/generators/git-commit",
      "category": "developer",
      "componentName": "GitCommitGenerator",
      "isImplemented": true
    },
    {
      "title": "Regex Builder",
      "icon": "fas fa-hammer",
      "description": "Visually build regex.",
      "url": "/generators/regex-builder",
      "category": "generators",
      "componentName": "RegexBuilder",
      "isImplemented": false
    },
    {
      "title": "SVG Pattern Generator",
      "icon": "fas fa-paint-brush",
      "description": "Generate SVG background patterns.",
      "url": "/generators/svg-pattern",
      "category": "generators",
      "componentName": "SvgPatternGenerator",
      "isImplemented": false
    },
    {
      "title": "Color Palette Generator",
      "icon": "fas fa-palette",
      "description": "Generate harmonious colors.",
      "url": "/design/palette",
      "category": "developer",
      "componentName": "ColorPaletteGenerator",
      "isImplemented": false
    },
    {
      "title": "Color Contrast Checker",
      "icon": "fas fa-adjust",
      "description": "Check WCAG compliance.",
      "url": "/design/contrast",
      "category": "accessibility",
      "componentName": "ColorContrastChecker",
      "isImplemented": true
    },
    {
      "title": "CSS Box Shadow Generator",
      "icon": "far fa-square",
      "description": "Generate CSS box shadows.",
      "url": "/design/box-shadow",
      "category": "developer",
      "componentName": "BoxShadowGenerator",
      "isImplemented": false
    },
    {
      "title": "CSS Gradient Generator",
      "icon": "fas fa-fill-drip",
      "description": "Generate CSS gradients.",
      "url": "/design/gradient",
      "category": "developer",
      "componentName": "GradientGenerator",
      "isImplemented": false
    },
    {
      "title": "CSS Border Radius Generator",
      "icon": "fas fa-circle-notch",
      "description": "Generate complex border radii.",
      "url": "/design/border-radius",
      "category": "developer",
      "componentName": "BorderRadiusGenerator",
      "isImplemented": false
    },
    {
      "title": "Image to Base64",
      "icon": "fas fa-image",
      "description": "Convert image to base64 string.",
      "url": "/design/image-base64",
      "category": "developer",
      "componentName": "ImageToBase64",
      "isImplemented": true
    },
    {
      "title": "Base64 to Image",
      "icon": "fas fa-image",
      "description": "Convert base64 string to image.",
      "url": "/design/base64-image",
      "category": "developer",
      "componentName": "Base64ToImage",
      "isImplemented": false
    },
    {
      "title": "SVG Optimizer",
      "icon": "fas fa-compress",
      "description": "Optimize SVG vectors.",
      "url": "/design/svg-optimizer",
      "category": "developer",
      "componentName": "SvgOptimizer",
      "isImplemented": false
    },
    {
      "title": "Typography Scale Calculator",
      "icon": "fas fa-font",
      "description": "Calculate font size scales.",
      "url": "/design/typography",
      "category": "developer",
      "componentName": "TypographyCalculator",
      "isImplemented": false
    },
    {
      "title": "Aspect Ratio Calculator",
      "icon": "fas fa-crop-alt",
      "description": "Calculate element proportions.",
      "url": "/design/aspect-ratio",
      "category": "developer",
      "componentName": "AspectRatioCalculator",
      "isImplemented": false
    },
    {
      "title": "CSS Selectors Tester",
      "icon": "fas fa-mouse-pointer",
      "description": "Test CSS selectors against DOM.",
      "url": "/design/css-selectors",
      "category": "developer",
      "componentName": "CssSelectorsTester",
      "isImplemented": false
    },
    {
      "title": "Web Font Pre-loader",
      "icon": "fas fa-text-height",
      "description": "Test custom fonts locally.",
      "url": "/design/web-font",
      "category": "developer",
      "componentName": "WebFontTester",
      "isImplemented": false
    },
    {
      "title": "Variable Case Converter",
      "icon": "fas fa-text-height",
      "description": "Convert case styles.",
      "url": "/text/case-converter",
      "category": "code",
      "componentName": "VariableCaseConverter",
      "isImplemented": true
    },
    {
      "title": "Diff Checker",
      "icon": "fas fa-not-equal",
      "description": "Check text differences.",
      "url": "/text/diff",
      "category": "code",
      "componentName": "DiffChecker",
      "isImplemented": false
    },
    {
      "title": "Word & Character Counter",
      "icon": "fas fa-calculator",
      "description": "Count words, chars, paragraphs.",
      "url": "/text/counter",
      "category": "time-number",
      "componentName": "WordCounter",
      "isImplemented": true
    },
    {
      "title": "Find and Replace",
      "icon": "fas fa-search",
      "description": "Find and replace text.",
      "url": "/text/find-replace",
      "category": "time-number",
      "componentName": "FindAndReplace",
      "isImplemented": true
    },
    {
      "title": "Text Sorter",
      "icon": "fas fa-sort",
      "description": "Sort text alphabetically or by length.",
      "url": "/text/sorter",
      "category": "time-number",
      "componentName": "TextSorter",
      "isImplemented": true
    },
    {
      "title": "Text Deduplicator",
      "icon": "fas fa-filter",
      "description": "Remove duplicate lines.",
      "url": "/text/deduplicator",
      "category": "time-number",
      "componentName": "TextDeduplicator",
      "isImplemented": true
    },
    {
      "title": "Line Reverser",
      "icon": "fas fa-exchange-alt",
      "description": "Reverse line order.",
      "url": "/text/reverser",
      "category": "time-number",
      "componentName": "LineReverser",
      "isImplemented": true
    },
    {
      "title": "Prefix/Suffix Adder",
      "icon": "fas fa-plus",
      "description": "Add prefix or suffix to lines.",
      "url": "/text/prefix-suffix",
      "category": "time-number",
      "componentName": "PrefixSuffixAdder",
      "isImplemented": true
    },
    {
      "title": "String Inspector",
      "icon": "fas fa-search-plus",
      "description": "Inspect characters in string.",
      "url": "/text/string-inspector",
      "category": "time-number",
      "componentName": "StringInspector",
      "isImplemented": false
    },
    {
      "title": "Cron Generator / Parser",
      "icon": "fas fa-calendar-alt",
      "description": "Parse and generate crons.",
      "url": "/system/cron",
      "category": "time-number",
      "componentName": "CronGenerator",
      "isImplemented": true
    },
    {
      "title": "Subnet Calculator",
      "icon": "fas fa-network-wired",
      "description": "Calculate IPs and Subnets.",
      "url": "/system/subnet",
      "category": "network",
      "componentName": "SubnetCalculator",
      "isImplemented": false
    },
    {
      "title": "Time Zone Converter",
      "icon": "fas fa-clock",
      "description": "Convert between timezones.",
      "url": "/system/timezone",
      "category": "time-number",
      "componentName": "TimeZoneConverter",
      "isImplemented": false
    },
    {
      "title": "Duration Calculator",
      "icon": "fas fa-stopwatch",
      "description": "Calculate duration between dates.",
      "url": "/system/duration",
      "category": "time-number",
      "componentName": "DurationCalculator",
      "isImplemented": true
    },
    {
      "title": "Timestamp Converter",
      "icon": "fas fa-hourglass",
      "description": "Convert UNIX timestamps.",
      "url": "/system/timestamp",
      "category": "time-number",
      "componentName": "TimestampConverter",
      "isImplemented": true
    },
    {
      "title": "Data Size Converter",
      "icon": "fas fa-database",
      "description": "Convert Byte sizes.",
      "url": "/system/data-size",
      "category": "data",
      "componentName": "DataSizeConverter",
      "isImplemented": true
    },
    {
      "title": "Unit Converter",
      "icon": "fas fa-ruler",
      "description": "Convert standard units.",
      "url": "/system/unit",
      "category": "developer",
      "componentName": "UnitConverter",
      "isImplemented": true
    },
    {
      "title": "Number Base Converter",
      "icon": "fas fa-calculator",
      "description": "Convert Base (Binary/Hex/Dec).",
      "url": "/system/base-converter",
      "category": "time-number",
      "componentName": "NumberBaseConverter",
      "isImplemented": true
    },
    {
      "title": "Bitwise Calculator",
      "icon": "fas fa-calculator",
      "description": "Perform bitwise math.",
      "url": "/system/bitwise",
      "category": "time-number",
      "componentName": "BitwiseCalculator",
      "isImplemented": true
    },
    {
      "title": "Code Complexity Analyzer",
      "icon": "fas fa-chart-line",
      "description": "Analyze JS complexity.",
      "url": "/system/complexity",
      "category": "student",
      "componentName": "CodeComplexityAnalyzer",
      "isImplemented": true
    },
    {
      "title": "Linux Permissions Calculator",
      "icon": "fab fa-linux",
      "description": "Calculate chmod values.",
      "url": "/system/chmod",
      "category": "developer",
      "componentName": "ChmodCalculator",
      "isImplemented": true
    },
    {
      "title": "IPv4 to IPv6 Converter",
      "icon": "fas fa-network-wired",
      "description": "Convert IPv4 to mapped IPv6.",
      "url": "/system/ipv6",
      "category": "network",
      "componentName": "Ipv4ToIpv6",
      "isImplemented": true
    },
    {
      "title": "CIDR Subnet Calculator",
      "icon": "fas fa-network-wired",
      "description": "Calculate CIDR ranges.",
      "url": "/system/cidr",
      "category": "network",
      "componentName": "CidrCalculator",
      "isImplemented": false
    },
    {
      "title": "MIME Types Lookup",
      "icon": "fas fa-file",
      "description": "Look up MIME types by extension.",
      "url": "/system/mime",
      "category": "developer",
      "componentName": "MimeLookup",
      "isImplemented": true
    },
    {
      "title": "User-Agent Parser",
      "icon": "fas fa-laptop",
      "description": "Parse User-Agent strings.",
      "url": "/system/user-agent",
      "category": "network",
      "componentName": "UserAgentParser",
      "isImplemented": true
    },
    {
      "title": "Regex Tester",
      "icon": "fas fa-search",
      "description": "Test regular expressions.",
      "url": "/testers/regex",
      "category": "code",
      "componentName": "RegexTester",
      "isImplemented": true
    },
    {
      "title": "OpenAPI Spec Validator",
      "icon": "fas fa-shield-alt",
      "description": "Validate Swagger/OpenAPI.",
      "url": "/testers/openapi",
      "category": "developer",
      "componentName": "OpenApiValidator",
      "isImplemented": false
    },
    {
      "title": "Code Diff Merge",
      "icon": "fas fa-code-branch",
      "description": "Diff and merge codes.",
      "url": "/testers/diff-merge",
      "category": "code",
      "componentName": "CodeDiffMerge",
      "isImplemented": true
    },
    {
      "title": "HTML Accessibility Checker",
      "icon": "fas fa-wheelchair",
      "description": "Check basic HTML a11y.",
      "url": "/testers/accessibility",
      "category": "accessibility",
      "componentName": "AccessibilityChecker",
      "isImplemented": false
    },
    {
      "title": "JSONPath Tester",
      "icon": "fas fa-search",
      "description": "Test JSONPath against data.",
      "url": "/testers/jsonpath",
      "category": "developer",
      "componentName": "JsonPathTester",
      "isImplemented": false
    },
    {
      "title": "JSON Schema Validator",
      "icon": "fas fa-check-square",
      "description": "Validate JSON vs Schema.",
      "url": "/testers/json-schema",
      "category": "developer",
      "componentName": "JsonSchemaValidator",
      "isImplemented": false
    },
    {
      "title": "AST Viewer",
      "icon": "fas fa-tree",
      "description": "View Abstract Syntax Tree.",
      "url": "/testers/ast-viewer",
      "category": "developer",
      "componentName": "AstViewer",
      "isImplemented": false
    },
    {
      "title": "HTTP Headers Parser",
      "icon": "fas fa-list-alt",
      "description": "Parse Raw HTTP Headers.",
      "url": "/testers/http-headers",
      "category": "network",
      "componentName": "HttpHeadersParser",
      "isImplemented": true
    },
    {
      "title": "SQL Query Syntax Checker",
      "icon": "fas fa-database",
      "description": "Check basic SQL syntax.",
      "url": "/testers/sql-syntax",
      "category": "data",
      "componentName": "SqlSyntaxChecker",
      "isImplemented": false
    },
    {
      "title": "HTTP Status Code Lookup",
      "icon": "fas fa-globe",
      "description": "Lookup HTTP statuses.",
      "url": "/cheatsheets/http-status",
      "category": "network",
      "componentName": "HttpStatusLookup",
      "isImplemented": true
    },
    {
      "title": "ASCII Table Reference",
      "icon": "fas fa-table",
      "description": "Standard ASCII table.",
      "url": "/cheatsheets/ascii",
      "category": "developer",
      "componentName": "AsciiTable",
      "isImplemented": true
    },
    {
      "title": "Git Command Cheat Sheet",
      "icon": "fab fa-git",
      "description": "Common git commands.",
      "url": "/cheatsheets/git",
      "category": "developer",
      "componentName": "GitCheatSheet",
      "isImplemented": true
    }
  ]
};
  
  // Filter for search and implemented tools
  const filteredTools = toolsData.tools.filter(tool => 
    tool.isImplemented && 
    (tool.title.toLowerCase().includes(search.toLowerCase()) || 
     tool.description.toLowerCase().includes(search.toLowerCase()))
  );

  const handleNav = (url) => {
    window.history.pushState(null, "", url);
    const navEvent = new PopStateEvent('popstate');
    window.dispatchEvent(navEvent);
    setMobileOpen(false);
  };

  useEffect(() => {
    const currentTool = toolsData.tools.find(t => t.url === currentRoute);
    if (currentTool) {
      document.title = currentTool.title + " | DevSuite - Tools for Developers";
      
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = currentTool.description;
    } else {
      document.title = "DevSuite - Tools for Developers";
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.content = "DevSuite - A comprehensive suite of developer tools.";
    }
  }, [currentRoute]);

  const renderTool = () => {
    switch (currentRoute) {
      case '/code/formatters/json': return <JsonBeautifier />;
      case '/code/formatters/xml': return <XmlBeautifier />;
      case '/code/formatters/toml': return <TomlBeautifier />;
      case '/code/formatters/sql': return <SqlBeautifier />;
      case '/code/formatters/html': return <HtmlBeautifier />;
      case '/code/formatters/css': return <CssBeautifier />;
      case '/code/formatters/javascript': return <JavascriptBeautifier />;
      case '/code/formatters/ini': return <IniBeautifier />;
      case '/code/minifiers/json': return <JsonMinifier />;
      case '/code/minifiers/xml': return <XmlMinifier />;
      case '/code/minifiers/css': return <CssMinifier />;
      case '/code/minifiers/javascript': return <JavascriptMinifier />;
      case '/code/minifiers/html': return <HtmlMinifier />;
      case '/code/converters/json-yaml': return <JsonToYamlConverter />;
      case '/code/converters/json-toml': return <JsonToTomlConverter />;
      case '/code/converters/json-csv': return <JsonToCsvConverter />;
      case '/code/converters/csv-tsv': return <CsvToTsvConverter />;
      case '/code/converters/json-ini': return <JsonToIniConverter />;
      case '/code/converters/json-sql': return <JsonToSqlConverter />;
      case '/code/converters/html-jsx': return <HtmlToJsxConverter />;
      case '/code/converters/markdown-html': return <MarkdownToHtmlConverter />;
      case '/encoders/base64': return <Base64EncoderDecoder />;
      case '/encoders/url': return <UrlEncoderDecoder />;
      case '/encoders/html-entities': return <HtmlEntitiesEncoderDecoder />;
      case '/encoders/jwt': return <JwtDecoderEncoder />;
      case '/encoders/aes': return <AesEncryptDecrypt />;
      case '/encoders/string-hex': return <StringToHexConverter />;
      case '/encoders/string-binary': return <StringToBinaryConverter />;
      case '/encoders/url-parser': return <UrlParser />;
      case '/hashes/md5': return <Md5Generator />;
      case '/hashes/sha1': return <Sha1Generator />;
      case '/hashes/sha256': return <Sha256Generator />;
      case '/hashes/sha3': return <Sha3Generator />;
      case '/hashes/sha512': return <Sha512Generator />;
      case '/hashes/bcrypt': return <BcryptGenerator />;
      case '/generators/uuid': return <UuidGenerator />;
      case '/generators/password': return <PasswordGenerator />;
      case '/generators/htaccess': return <HtaccessGenerator />;
      case '/generators/qrcode': return <QrCodeGenerator />;
      case '/generators/morse': return <MorseCodeConverter />;
      case '/generators/robots-txt': return <RobotsTxtGenerator />;
      case '/generators/sitemap': return <SitemapGenerator />;
      case '/generators/lorem-ipsum': return <LoremIpsumGenerator />;
      case '/generators/dummy-json': return <DummyJsonGenerator />;
      case '/generators/docker-compose': return <DockerComposeGenerator />;
      case '/generators/git-commit': return <GitCommitGenerator />;
      case '/design/contrast': return <ColorContrastChecker />;
      case '/design/image-base64': return <ImageToBase64 />;
      case '/text/case-converter': return <VariableCaseConverter />;
      case '/text/counter': return <WordCounter />;
      case '/text/find-replace': return <FindAndReplace />;
      case '/text/sorter': return <TextSorter />;
      case '/text/deduplicator': return <TextDeduplicator />;
      case '/text/reverser': return <LineReverser />;
      case '/text/prefix-suffix': return <PrefixSuffixAdder />;
      case '/system/cron': return <CronGenerator />;
      case '/system/duration': return <DurationCalculator />;
      case '/system/timestamp': return <TimestampConverter />;
      case '/system/data-size': return <DataSizeConverter />;
      case '/system/unit': return <UnitConverter />;
      case '/system/base-converter': return <NumberBaseConverter />;
      case '/system/bitwise': return <BitwiseCalculator />;
      case '/system/complexity': return <CodeComplexityAnalyzer />;
      case '/system/chmod': return <ChmodCalculator />;
      case '/system/ipv6': return <Ipv4ToIpv6 />;
      case '/system/mime': return <MimeLookup />;
      case '/system/user-agent': return <UserAgentParser />;
      case '/testers/regex': return <RegexTester />;
      case '/testers/diff-merge': return <CodeDiffMerge />;
      case '/testers/http-headers': return <HttpHeadersParser />;
      case '/cheatsheets/http-status': return <HttpStatusLookup />;
      case '/cheatsheets/ascii': return <AsciiTable />;
      case '/cheatsheets/git': return <GitCheatSheet />;
      default: return renderDashboard();
    }
  };


  const renderDashboard = () => {
    if (filteredTools.length === 0) {
      return (
        <Box sx={{ mt: 10, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary">
            No tools found matching "{search}"
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {toolsData.categories.map((category) => {
            const catTools = filteredTools.filter(t => t.category === category.id);
            if (catTools.length === 0) return null;
            
            return (
              <Box key={category.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex', mr: 2 }}>
                     {categoryIcons[category.id] || <CodeIcon fontSize="large" />}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {category.displayName}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                  {catTools.map((tool, idx) => (
                    <Card key={idx} elevation={2} sx={{ height: 170, minHeight: 170, maxHeight: 170, overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 } }}>
                      <CardActionArea onClick={() => handleNav(tool.url)} sx={{ flexGrow: 1, height: '100%', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, width: '100%', height: 48 }}>
                          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', mr: 2, display: 'flex' }}>
                            {categoryIcons[tool.category] || <CodeIcon />}
                          </Box>
                          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {tool.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 1 }}>
                          {tool.description}
                        </Typography>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
    );
  };

  const drawer = (
    <div>
      <Toolbar />
      <Box sx={{ overflow: 'auto', mt: 2 }}>
        <List>
          <ListItem button onClick={() => handleNav('/')} sx={{ mb: 1, borderRadius: 2, mx: 1, width: 'auto' }}>
            <ListItemIcon><HomeIcon color="primary" /></ListItemIcon>
            <ListItemText primary="All Tools Dashboard" primaryTypographyProps={{ fontWeight: 'bold' }} />
          </ListItem>
        </List>
        <Divider sx={{ mx: 2, my: 1 }} />
        {toolsData.categories.map((category) => {
          const catTools = filteredTools.filter(t => t.category === category.id);
          if (catTools.length === 0) return null;
          return (
            <React.Fragment key={category.id}>
              <Typography variant="overline" sx={{ px: 3, pt: 2, pb: 1, display: 'block', color: 'text.secondary', fontWeight: 'bold' }}>
                {category.displayName}
              </Typography>
              <List disablePadding>
                {catTools.map((tool) => (
                  <ListItem 
                    button 
                    key={tool.url} 
                    onClick={() => handleNav(tool.url)}
                    selected={currentRoute === tool.url}
                    sx={{ 
                      borderRadius: 2, 
                      mx: 1, 
                      mb: 0.5, 
                      width: 'auto',
                      '&.Mui-selected': { bgcolor: 'primary.light', color: 'primary.contrastText' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: currentRoute === tool.url ? 'inherit' : 'text.secondary' }}>
                      {categoryIcons[tool.category] || <CodeIcon />}
                    </ListItemIcon>
                    <ListItemText primary={tool.title} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </ListItem>
                ))}
              </List>
            </React.Fragment>
          );
        })}
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexGrow: 1 }}>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="tool categories"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid rgba(255,255,255,0.1)' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
        <Box
          component="main"
          sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, pt: 1.5, px: 1.5, pb: 0 }}
        >
        <Toolbar /> 
        <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, justifyContent: 'space-between' }}>
          
          {/* Left Section: Active Tool or Dashboard */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, width: { lg: '75%' }, maxWidth: { lg: '75%' } }}>
            {renderTool()}
          </Box>

          {/* Right Section: Global Persistent Ad Space */}
          <Box sx={{ width: { xs: '100%', lg: '25%' }, display: { xs: 'none', lg: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 76, height: 'calc(100vh - 140px)', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
               <Card elevation={1} sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                 <AdUnit />
               </Card>
            </Box>
          </Box>

          </Box>
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default MainContent;
